/**
 * Analytics Hub - View analytics by batch or individual tags
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart3, ArrowLeft, FolderOpen, Tags, Eye, Users, 
  TrendingUp, Monitor, Smartphone, Tablet, Globe, ChevronRight,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/analytics-overview`);
      setOverview(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchAnalytics = async (batchName) => {
    try {
      setLoadingBatch(true);
      setSelectedBatch(batchName);
      const response = await axios.get(`${BACKEND_URL}/api/admin/analytics-batch/${encodeURIComponent(batchName)}`);
      setBatchData(response.data);
    } catch (error) {
      toast.error('Failed to load batch analytics');
    } finally {
      setLoadingBatch(false);
    }
  };

  const closeBatchView = () => {
    setSelectedBatch(null);
    setBatchData(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              <h1 className="text-3xl sm:text-4xl font-bold">Analytics Hub</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              View analytics by batch/campaign or individual tags
            </p>
          </div>

          {/* Overall Stats */}
          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm">Total Views</span>
                </div>
                <p className="text-3xl font-bold">{overview.totals.total_views.toLocaleString()}</p>
              </div>
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Unique Visitors</span>
                </div>
                <p className="text-3xl font-bold">{overview.totals.unique_visitors.toLocaleString()}</p>
              </div>
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Tags className="w-4 h-4" />
                  <span className="text-sm">Total Tags</span>
                </div>
                <p className="text-3xl font-bold">{overview.totals.total_tags}</p>
              </div>
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <FolderOpen className="w-4 h-4" />
                  <span className="text-sm">Batches</span>
                </div>
                <p className="text-3xl font-bold">{overview.totals.total_batches}</p>
              </div>
            </div>
          )}

          {/* Batch/Tag Selection or Detail View */}
          {!selectedBatch ? (
            // Batch List View
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="p-5 border-b border-border">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  Batches & Campaigns
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Click a batch to see detailed analytics
                </p>
              </div>
              <div className="divide-y divide-border">
                {overview?.batches?.map((batch) => (
                  <button
                    key={batch.batch_name}
                    onClick={() => loadBatchAnalytics(batch.batch_name)}
                    className="w-full p-5 hover:bg-secondary/30 transition-colors text-left flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        batch.batch_name === 'uncategorized' 
                          ? 'bg-secondary text-muted-foreground' 
                          : 'bg-primary/10 text-primary'
                      }`}>
                        <FolderOpen className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">
                          {batch.batch_name === 'uncategorized' ? 'Uncategorized' : batch.batch_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {batch.tag_count} tags
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="font-bold text-lg">{batch.total_views.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">views</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{batch.unique_visitors.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">unique</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </div>
                  </button>
                ))}
                {overview?.batches?.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No batches found. Create tags and organize them into batches.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Batch Detail View
            <div className="space-y-6">
              {/* Back button */}
              <button
                onClick={closeBatchView}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all batches
              </button>

              {loadingBatch ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : batchData && (
                <>
                  {/* Batch Header */}
                  <div className="bg-card border border-border rounded-2xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                        <FolderOpen className="w-7 h-7 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">
                          {selectedBatch === 'uncategorized' ? 'Uncategorized' : selectedBatch}
                        </h2>
                        <p className="text-muted-foreground">{batchData.tag_count} tags in this batch</p>
                      </div>
                    </div>
                    
                    {/* Batch Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-secondary/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Eye className="w-4 h-4" />
                          <span className="text-xs">Total Views</span>
                        </div>
                        <p className="text-2xl font-bold">{batchData.stats.total_views.toLocaleString()}</p>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Users className="w-4 h-4" />
                          <span className="text-xs">Unique Visitors</span>
                        </div>
                        <p className="text-2xl font-bold">{batchData.stats.unique_visitors.toLocaleString()}</p>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Smartphone className="w-4 h-4" />
                          <span className="text-xs">Mobile</span>
                        </div>
                        <p className="text-2xl font-bold">{batchData.device_breakdown.mobile}</p>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Monitor className="w-4 h-4" />
                          <span className="text-xs">Desktop</span>
                        </div>
                        <p className="text-2xl font-bold">{batchData.device_breakdown.desktop}</p>
                      </div>
                    </div>
                  </div>

                  {/* Top Locations */}
                  {Object.keys(batchData.top_locations).length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-6">
                      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5" />
                        Top Locations
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                        {Object.entries(batchData.top_locations).slice(0, 5).map(([country, count]) => (
                          <div key={country} className="bg-secondary/30 rounded-xl p-3 text-center">
                            <p className="font-bold text-lg">{count}</p>
                            <p className="text-xs text-muted-foreground truncate">{country}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tags in Batch */}
                  <div className="bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="p-5 border-b border-border">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <Tags className="w-5 h-5" />
                        Tags Performance
                      </h3>
                    </div>
                    <div className="divide-y divide-border">
                      {batchData.tags_breakdown.map((tag) => (
                        <div
                          key={tag.nfc_code}
                          className="p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                              <Tags className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{tag.description || 'Untitled'}</p>
                              <p className="text-xs text-muted-foreground font-mono">{tag.nfc_code}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="font-bold">{tag.views}</p>
                              <p className="text-xs text-muted-foreground">views</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{tag.unique_visitors}</p>
                              <p className="text-xs text-muted-foreground">unique</p>
                            </div>
                            <button
                              onClick={() => navigate(`/admin/analytics/${tag.nfc_code}`)}
                              className="px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
                            >
                              Details
                            </button>
                          </div>
                        </div>
                      ))}
                      {batchData.tags_breakdown.length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          <Tags className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No tags in this batch</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AnalyticsHub;
